import React, { useEffect, useState, useRef } from "react";
import { Marker, Circle, Polyline } from "react-leaflet";
import LeafletMap, { liveDotIcon, Recenter, useUserFallbackCenter } from "../components/LeafletMap";
import { api } from "../api";
import { useAuth } from "../auth";
import { Gauge, Activity, Clock, Route, Wifi, WifiOff, Share2, Car, Radio, ArrowRight, Crosshair } from "lucide-react";
import ShareSheet from "../components/ShareSheet";
import SOSButton from "../components/SOSButton";
import DevicePicker from "../components/DevicePicker";
import { useDevices } from "../devices";

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}

function formatIST(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

export default function HomeScreen() {
  const { t } = useAuth();
  const { selectedDevice, selectedDeviceId } = useDevices();
  const deviceId = selectedDeviceId;
  const fallbackCenter = useUserFallbackCenter();
  const deviceName = selectedDevice?.name || "";
  const [live, setLive] = useState(null);
  const [fences, setFences] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [shareOpen, setShareOpen] = useState(false);
  // The map stops chasing the car once the user pans; this drives the
  // "recenter" affordance that puts it back on the vehicle.
  const [following, setFollowing] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const loadLive = async (id) => {
    try {
      const { data } = await api.get(`/location/live/${id}`);
      setLive(data);
    } catch (_) {}
  };
  const loadFences = async (id) => {
    try {
      const { data } = await api.get(`/geofence/${id}`);
      setFences(data);
    } catch (_) {}
  };

  useEffect(() => {
    if (!deviceId) {
      setLive(null); setFences([]);
      return;
    }
    setLive(null);
    loadLive(deviceId);
    loadFences(deviceId);
    timerRef.current = setInterval(() => loadLive(deviceId), 10000);
    return () => clearInterval(timerRef.current);
  }, [deviceId]);

  const loc = live?.last_location;
  const speed = Math.round(loc?.speed || 0);
  const moving = speed >= 5;
  const center = loc ? [loc.latitude, loc.longitude] : fallbackCenter;
  const isOnline = online && live?.is_online;
  const hasEverConnected = !!loc;

  if (deviceId && !hasEverConnected) {
    return (
      <div data-testid="home-waiting" className="min-h-[100dvh] bg-surface-soft flex flex-col items-center justify-center px-6 text-center pb-24">
        <div className="relative mb-7">
          <div className="w-28 h-28 rounded-3xl bg-gradient-to-tr from-brand-500/10 to-accent-cyan/10 flex items-center justify-center border border-brand-500/10">
            <Car size={44} className="text-brand-400" />
          </div>
          <div className="absolute -right-2 -top-2 w-9 h-9 rounded-xl bg-alert/10 border border-alert/20 flex items-center justify-center">
            <Radio size={16} className="text-alert animate-pulse" />
          </div>
        </div>
        <h1 className="text-[22px] font-heading font-extrabold text-ink">{t.waitingTitle}</h1>
        <p className="mt-2 text-[13px] text-ink-soft max-w-[280px] leading-relaxed">{t.waitingBody}</p>
        <div className="mt-6 bg-white border border-surface-border rounded-2xl px-5 py-4 text-left w-full max-w-xs shadow-card-sm">
          <div className="text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-1">{t.linkedAs}</div>
          <div className="font-mono font-bold text-[14px] text-ink">{deviceName}</div>
          <div className="text-[11px] text-ink-muted font-mono mt-0.5">{deviceId}</div>
        </div>
        <div className="mt-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-alert/10 border border-alert/15">
          <span className="w-2 h-2 rounded-full bg-alert animate-pulse" />
          <span className="text-[12px] font-semibold text-amber-700">{t.disconnected}</span>
        </div>
      </div>
    );
  }

  if (!deviceId) {
    return (
      <div data-testid="home-no-device" className="min-h-[100dvh] bg-surface-soft flex flex-col items-center justify-center px-6 text-center pb-24">
        <div className="w-28 h-28 rounded-3xl bg-gradient-to-tr from-brand-500/10 to-accent-cyan/10 flex items-center justify-center border border-brand-500/10 mb-7">
          <Car size={44} className="text-brand-300" />
        </div>
        <h1 className="text-[22px] font-heading font-extrabold text-ink">{t.linkDevice}</h1>
        <p className="mt-2 text-[13px] text-ink-soft max-w-[260px] leading-relaxed">{t.deviceIdHelp}</p>
        <button
          data-testid="home-link-cta"
          onClick={() => window.location.assign("/link")}
          className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 text-white text-[14px] font-bold shadow-brand hover:opacity-95 transition-opacity shimmer-btn cursor-pointer"
        >
          {t.link}
          <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="home-screen" className="relative h-[100vh] h-[100dvh] w-full overflow-hidden">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-[450] px-4 pt-4">
        <div className="glass-card rounded-2xl px-4 py-3 flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{t.carLive}</div>
            <div className="text-[15px] font-heading font-extrabold text-slate-900 truncate" data-testid="home-car-name">
              {deviceName || "—"}
            </div>
          </div>
          <DevicePicker className="mr-2" />
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-bold ${
              isOnline
                ? "bg-emerald-50 text-moving border border-emerald-100"
                : "bg-red-50 text-parked border border-red-100"
            }`}
            data-testid="home-status"
          >
            {isOnline ? <Wifi size={13} /> : <WifiOff size={13} />}
            {isOnline ? t.connected : t.disconnected}
          </div>
        </div>
        {deviceId && (
          <div className="mt-2 flex justify-end">
            <SOSButton deviceId={deviceId} variant="fab" />
          </div>
        )}
        {!online && (
          <div data-testid="offline-banner" className="mt-2 text-center text-[12px] bg-alert/10 text-amber-700 rounded-xl py-1.5 font-semibold border border-alert/15">
            {t.noInternet}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="absolute inset-0">
        <LeafletMap center={center} zoom={14} testid="home-map">
          {loc && (
            <>
              <Marker position={[loc.latitude, loc.longitude]} icon={liveDotIcon} />
              <Recenter
                lat={loc.latitude}
                lon={loc.longitude}
                zoom={14}
                follow={following}
                onUserMove={() => setFollowing(false)}
              />
            </>
          )}
          {fences.map((f) => (
            <Circle
              key={f.id}
              center={[f.latitude, f.longitude]}
              radius={f.radius}
              pathOptions={{ color: "#4f46e5", fillColor: "#4f46e5", fillOpacity: 0.08, weight: 2 }}
            />
          ))}
        </LeafletMap>
      </div>

      {/* Floating status card */}
      <div
        className="absolute left-3 right-3 z-[450]"
        style={{ bottom: "calc(88px + env(safe-area-inset-bottom))" }}
      >
        <div className="glass-card rounded-2xl p-4 animate-fadeIn">
          <div className="grid grid-cols-4 gap-2.5">
            <Stat
              testid="stat-speed"
              icon={<Gauge size={16} />}
              tint="brand"
              label={t.currentSpeed}
              value={`${speed}`}
              suffix="km/h"
            />
            <Stat
              testid="stat-engine"
              icon={<Activity size={16} />}
              tint={moving ? "emerald" : "rose"}
              label={t.engineStatus}
              value={moving ? t.moving : t.parked}
              valueColor={moving ? "text-moving" : "text-parked"}
              small
            />
            <Stat
              testid="stat-distance"
              icon={<Route size={16} />}
              tint="brand"
              label={t.todayDistance}
              value={`${live?.today_distance_km ?? 0}`}
              suffix="km"
            />
            <Stat
              testid="stat-updated"
              icon={<Clock size={16} />}
              tint="slate"
              label={t.lastUpdated}
              value={loc ? timeAgo(loc.timestamp) : "—"}
              small
            />
          </div>

          <div className="mt-3 pt-3 border-t border-slate-900/[0.06] flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-ink-soft min-w-0">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-600 animate-pulse shrink-0" />
              <span className="truncate">{t.live} · IST {formatIST(loc?.timestamp)}</span>
            </span>

            {/* Only offered once the user has actually panned away */}
            {!following ? (
              <button
                data-testid="recenter-btn"
                onClick={() => setFollowing(true)}
                className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-50 text-brand-700 text-[11px] font-bold hover:bg-brand-100 transition-colors cursor-pointer"
              >
                <Crosshair size={12} />
                {t.recenter || "Recenter"}
              </button>
            ) : (
              <span className="shrink-0 font-mono tabular text-[10px] text-ink-muted">
                {loc ? `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}` : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Share FAB */}
      {deviceId && (
        <button
          data-testid="share-fab"
          onClick={() => setShareOpen(true)}
          className="absolute right-4 z-[460] bg-brand-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-brand active:scale-95 transition-transform"
          style={{ bottom: "calc(230px + env(safe-area-inset-bottom))" }}
          title={t.shareLive}
        >
          <Share2 size={20} />
        </button>
      )}

      <ShareSheet deviceId={deviceId} open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}

/** Icon tiles are tinted per metric so the four stats read as a set. */
const STAT_TINTS = {
  brand: "bg-brand-50 text-brand-600",
  emerald: "bg-emerald-50 text-moving",
  rose: "bg-red-50 text-parked",
  slate: "bg-surface-muted text-ink-soft",
};

function Stat({ icon, label, value, suffix, testid, tint = "brand", valueColor = "text-ink", small }) {
  return (
    <div data-testid={testid} className="flex flex-col min-w-0">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-1.5 ${STAT_TINTS[tint]}`}>
        {icon}
      </div>
      {/* .tabular stops the layout jittering as the speed digits tick */}
      <div className={`font-black tabular truncate ${small ? "text-[15px]" : "text-[21px]"} ${valueColor} leading-none`}>
        {value}
        {suffix && <span className="text-[10px] font-bold ml-0.5 text-ink-muted">{suffix}</span>}
      </div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-ink-muted mt-1 truncate">{label}</div>
    </div>
  );
}
